import { RewardSummaryDataState } from 'components/features/rewards/rewardPage/store'
import { InvestCryptoButton } from 'components/features/rewards/rewardPageSummary/InvestCryptoButton/InvestCryptoButton'
import { RedeemButton } from 'components/features/rewards/rewardPageSummary/RedeemButton/RedeemButton'
import { SellCryptoButton } from 'components/features/rewards/rewardPageSummary/SellCryptoButton/SellCryptoButton'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { FC, useMemo } from 'react'
import { minRedemptionAmountSelector, rewardSummarySelector } from 'selectors/bountySelectors'
import { AutoinvestTurnOnButton } from 'components/features/rewards/rewardPageSummary/AutoinvestTurnOnButton/AutoinvestTurnOnButton'
import { AutoinvestTurnOffButton } from 'components/features/rewards/rewardPageSummary/AutoinvestTurnOffButton/AutoinvestTurnOffButton'
import styles from 'components/features/rewards/rewardPageSummary/SummaryActions/SummaryActions.styl'
import cn from 'classnames'
import { useMinBuyCryptoAmount } from 'components/features/rewards/rewardPage/useBalance'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { cryptoMarketGuard } from 'components/features/rewards/rewardsPane/guards/cryptoCurrenciesMarketDataLoadedGuard'
import { cryptoMetaGuard } from 'components/features/rewards/rewardsPane/guards/cryptoMetaGuard'
import { getCryptoCurrenciesDefaultCurrencyIdOrNullSelector } from 'components/features/rewards/rewardsPane/store/selectors/currencies'

export const SummaryActions: FC = (props) => {
  const isMarketLoaded = useSimpleGuard(cryptoMarketGuard, props, 'SummaryActions')
  const isMetaLoaded = useSimpleGuard(cryptoMetaGuard, props, 'SummaryActions')

  const summary = useStoreSelector(rewardSummarySelector)
  const minRedemptionAmount = useStoreSelector(minRedemptionAmountSelector)

  if (summary === null || minRedemptionAmount === null || !isMarketLoaded || !isMetaLoaded) {
    return null
  }

  return (
    <SummaryActionsInner
      summary={ summary }
      minRedemptionAmount={ minRedemptionAmount }
    />
  )
}

type SummaryActionsInnerProps = {
  summary: RewardSummaryDataState
  minRedemptionAmount: number
}

const SummaryActionsInner: FC<SummaryActionsInnerProps> = (props) => {
  const { summary, minRedemptionAmount } = props
  const { is_autoinvest_on, cryptoBalance, usdBalance } = summary

  const defaultCrypto = useStoreSelector((state) => getCryptoCurrenciesDefaultCurrencyIdOrNullSelector(state) || 'BTC')
  const minUsdAmount = +useMinBuyCryptoAmount(defaultCrypto)
  const isInvestAvailable = usdBalance >= minUsdAmount && (usdBalance >= minRedemptionAmount || cryptoBalance > 0)

  const investButton = useMemo(() => {
    if (!is_autoinvest_on && isInvestAvailable) {
      return (
        <InvestCryptoButton />
      )
    }

    return null
  }, [is_autoinvest_on, isInvestAvailable])


  const turnOnAutoinvestButton = useMemo(() => {
    if (!is_autoinvest_on && !investButton) {
      return (
        <AutoinvestTurnOnButton />
      )
    }

    return null
  }, [is_autoinvest_on, investButton])


  const sellCryptoButton = useMemo(() => {
    if (cryptoBalance === 0) {
      return null
    }

    if (usdBalance === 0 && !is_autoinvest_on) {

      return (
        <SellCryptoButton size='tiny' style='textOnly' />
      )
    }

    return (
      <SellCryptoButton size='small' style='hollow' />
    )
  }, [cryptoBalance, usdBalance, is_autoinvest_on])

  const turnOffAutoinvestButton = useMemo(() => {
    if (is_autoinvest_on) {
      return (
        <div className={ cn(styles.autoinvestRadio, sellCryptoButton && styles.widthSell) }>
          <AutoinvestTurnOffButton />
        </div>
      )
    }

    return null
  }, [is_autoinvest_on, sellCryptoButton])

  const redeemButton = useMemo(() => {
    if (is_autoinvest_on || usdBalance < minRedemptionAmount) {
      return null
    }

    return (
      <RedeemButton />
    )
  }, [usdBalance, is_autoinvest_on, minRedemptionAmount])


  return (
    <div className={ styles.summaryActions }>
      { turnOnAutoinvestButton }
      { investButton }
      { sellCryptoButton }
      { redeemButton }
      { turnOffAutoinvestButton }
    </div>
  )
}
